import { useMemo, useState } from 'react'
import { ExternalLink, FileDown, Loader2, Package } from 'lucide-react'
import { toast } from 'sonner'
import type { FinanceProposalSnapshotRow } from '@/services/financeService'
import { Button } from '@/components/ui/Button'
import { getFriendlyError } from '@/utils/getFriendlyError'
import { FinanceDetailInfoRow, FinanceDetailSectionCard } from './FinanceDetailSectionCard'
import {
  buildClosedScope,
  buildClosedScopePlanViews,
  hasClosedScopeContent,
} from './financeClosedScope'
import {
  ClosedScopeItemDetailModal,
  FinanceClosedScopeLineRow,
} from './ClosedScopeItemDetailModal'
import { getFormFieldLabel } from './financeRequestFormDisplay'
import {
  downloadFinanceClosedScopeAnnexPdf,
  generateFinanceClosedScopeAnnexPdf,
} from './financeClosedScopeAnnexPdf'
import { cn } from '@/utils/cn'

type Props = {
  formData: Record<string, unknown>
  snapshot?: FinanceProposalSnapshotRow | null
  empresaNome?: string
  canViewFinancialValues: boolean
  showEmptyState?: boolean
  financeProtocol?: string
}

export function FinanceClosedScopeSection({
  formData,
  snapshot,
  empresaNome,
  canViewFinancialValues,
  showEmptyState = false,
  financeProtocol,
}: Props) {
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null)
  const [generatingAnnex, setGeneratingAnnex] = useState(false)
  const scope = buildClosedScope({
    formData,
    snapshot,
    includeValues: canViewFinancialValues,
  })
  const planViews = useMemo(() => buildClosedScopePlanViews(snapshot), [snapshot])
  const planById = useMemo(
    () => new Map(planViews.map((plan) => [plan.item.id, plan])),
    [planViews],
  )
  const selectedPlan = selectedPlanId ? planById.get(selectedPlanId) ?? null : null
  const canGenerateAnnex = Boolean(snapshot && scope.lines.length > 0 && canViewFinancialValues)

  const handleGenerateAnnex = async () => {
    if (!snapshot || !empresaNome) return
    setGeneratingAnnex(true)
    const toastId = toast.loading('Gerando Anexo 1 do contrato...')
    try {
      const blob = await generateFinanceClosedScopeAnnexPdf(snapshot, formData, empresaNome)
      downloadFinanceClosedScopeAnnexPdf(blob, empresaNome, financeProtocol)
      toast.success('Anexo 1 gerado com sucesso', { id: toastId })
    } catch (err: unknown) {
      toast.error(getFriendlyError(err, 'Erro ao gerar Anexo 1'), { id: toastId })
    } finally {
      setGeneratingAnnex(false)
    }
  }

  if (!hasClosedScopeContent(scope)) {
    if (!showEmptyState) return null
    return (
      <FinanceDetailSectionCard title='Escopo do fechamento' icon={Package} accent='#6366f1'>
        <p className='text-sm text-text-muted'>
          Nenhum item de escopo registrado neste fechamento ainda.
        </p>
      </FinanceDetailSectionCard>
    )
  }

  return (
    <FinanceDetailSectionCard
      title='Escopo do fechamento'
      icon={Package}
      accent='#6366f1'
      headerExtra={
        canGenerateAnnex ? (
          <Button
            type='button'
            size='sm'
            variant='secondary'
            disabled={generatingAnnex}
            onClick={() => void handleGenerateAnnex()}
          >
            {generatingAnnex ? (
              <Loader2 size={14} className='animate-spin' />
            ) : (
              <FileDown size={14} />
            )}
            Gerar Anexo 1
          </Button>
        ) : null
      }
    >
      <div className='mb-4 space-y-1'>
        <p className='text-sm font-medium text-text-primary'>
          {empresaNome ? `Contratado para ${empresaNome}` : 'Itens e serviços fechados com o cliente'}
        </p>
        {(scope.versao != null || scope.createdAt) && (
          <p className='text-xs text-text-muted'>
            {scope.versao != null && `Versão ${scope.versao}`}
            {scope.createdAt &&
              `${scope.versao != null ? ' · ' : ''}${new Date(scope.createdAt).toLocaleString('pt-BR')}`}
          </p>
        )}
      </div>

      {scope.lines.length > 0 ? (
        <ul className='space-y-2'>
          {scope.lines.map((line) => {
            const plan = planById.get(line.id)
            return (
              <FinanceClosedScopeLineRow
                key={line.id}
                descricao={line.descricao}
                tipo={line.tipo}
                quantidade={line.quantidade}
                total={line.total}
                scopeCount={plan?.scopeCount ?? 0}
                canViewFinancialValues={canViewFinancialValues}
                onOpen={() => setSelectedPlanId(line.id)}
              />
            )
          })}
        </ul>
      ) : scope.servicosText ? (
        <p className='rounded-xl border border-white/[0.06] bg-white/[0.03] p-4 text-sm leading-relaxed text-text-primary'>
          {scope.servicosText}
        </p>
      ) : null}

      {scope.lines.length > 0 && scope.servicosText && (
        <div className='mt-4 border-t border-white/[0.06] pt-3'>
          <FinanceDetailInfoRow
            label={getFormFieldLabel('servicosContratados')}
            value={scope.servicosText}
          />
        </div>
      )}

      {scope.plataforma && (
        <div className={cn(scope.lines.length > 0 ? 'mt-3' : '')}>
          <FinanceDetailInfoRow label={getFormFieldLabel('plataforma')} value={scope.plataforma} />
        </div>
      )}

      {scope.observacoes && (
        <div className='mt-4 rounded-xl border border-white/[0.06] bg-white/[0.02] p-4'>
          <p
            className='mb-2 font-medium uppercase tracking-wider text-text-muted'
            style={{ fontSize: 'var(--font-size-label)' }}
          >
            Observações do fechamento
          </p>
          <p className='whitespace-pre-wrap text-sm leading-relaxed text-text-primary'>
            {scope.observacoes}
          </p>
        </div>
      )}

      {renderContact(formData.responsavelAssinatura, getFormFieldLabel('responsavelAssinatura'))}
      {renderContact(formData.responsavelFinanceiro, getFormFieldLabel('responsavelFinanceiro'))}

      {!canViewFinancialValues && (
        <p className='mt-4 text-xs leading-relaxed text-text-muted'>
          Valores e proposta detalhada são visíveis apenas para o time financeiro.
        </p>
      )}

      {canViewFinancialValues && scope.proposalPublicUrl && (
        <div className='mt-4 border-t border-white/[0.06] pt-3'>
          <a
            href={scope.proposalPublicUrl}
            target='_blank'
            rel='noreferrer'
            className='inline-flex items-center gap-1.5 text-xs font-semibold text-accent hover:underline'
          >
            <ExternalLink size={14} aria-hidden />
            Abrir proposta enviada ao cliente
          </a>
        </div>
      )}

      {selectedPlan && (
        <ClosedScopeItemDetailModal
          plan={selectedPlan}
          canViewFinancialValues={canViewFinancialValues}
          onClose={() => setSelectedPlanId(null)}
        />
      )}
    </FinanceDetailSectionCard>
  )
}

function renderContact(value: unknown, label: string) {
  if (!isContactRecord(value)) return null
  const text = formatContact(value)
  if (text === '—') return null
  return (
    <div className='mt-3'>
      <FinanceDetailInfoRow label={label} value={text} />
    </div>
  )
}

function isContactRecord(value: unknown): value is Record<string, unknown> {
  return value != null && typeof value === 'object' && !Array.isArray(value)
}

function formatContact(value: Record<string, unknown>): string {
  const nome = typeof value.nome === 'string' ? value.nome : ''
  const email = typeof value.email === 'string' ? value.email : ''
  if (nome && email) return `${nome} · ${email}`
  return nome || email || '—'
}
